import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Check, ChevronRight, Plus, Minus, MessageCircle,
  Building2, Fingerprint, User2, Phone as PhoneIcon, Mail as MailIcon,
  Calendar, MapPin, Layers, TrendingUp, MapPinned, Factory, Activity,
  Landmark, ShieldPlus, Cpu, PenTool, Scale, Briefcase, Megaphone, Percent,
} from "lucide-react";
import { useSeo } from "@/lib/seo";
import { BRAND } from "@/data/site";

const WA = BRAND.whatsapp.replace(/[^\d]/g, "");
const WA_URL = `https://wa.me/${WA}`;

const FIELDS = [
  { icon: Building2, name: "Business Name (Trade Name)", d: "The name under which the business operates" },
  { icon: Fingerprint, name: "GSTIN", d: "Unique 15-digit GST Identification Number (e.g. 27AABCU9603R1ZM)" },
  { icon: Layers, name: "Legal Name of Business", d: "The legal entity name as registered with GSTN" },
  { icon: User2, name: "Owner / Director Name", d: "Business owner, partner or director name" },
  { icon: PhoneIcon, name: "Mobile Number", d: "Verified mobile of the owner or authorised signatory" },
  { icon: MailIcon, name: "Email Address", d: "Business or owner email as filed with GST" },
  { icon: MapPin, name: "State", d: "State where the business is GST-registered" },
  { icon: MapPinned, name: "District / City", d: "Specific district or city of operation" },
  { icon: Factory, name: "Business Category", d: "Manufacturing, Trading or Services" },
  { icon: Calendar, name: "Registration Date", d: "Date when GST registration was granted" },
  { icon: Layers, name: "Business Type", d: "Proprietorship, Partnership, Pvt Ltd, LLP, Trust, HUF" },
  { icon: Percent, name: "Annual Turnover Slab", d: "Turnover band as declared to GSTN" },
  { icon: Activity, name: "Filing Status", d: "Whether the business is actively filing GST returns" },
];

const STATS_YEAR = [
  { y: "2022", r: "7,50,000+", m: "62,500", g: "—" },
  { y: "2023", r: "8,80,000+", m: "73,300", g: "+17.3%" },
  { y: "2024", r: "10,20,000+", m: "85,000", g: "+15.9%" },
  { y: "2025", r: "11,50,000+", m: "95,800", g: "+12.7%" },
  { y: "2026", r: "12,80,000+ (projected)", m: "1,06,600", g: "+11.3%" },
];

const HERO_STATS = [
  { n: "1.5 Crore+", l: "Total active GST registrations in India" },
  { n: "50,000-80,000", l: "New GST registrations every month" },
  { n: "28 + 8", l: "States + Union Territories covered" },
  { n: "12.8 Lakh+", l: "Projected new registrations in 2026" },
];

const USE_CASES = [
  { icon: Megaphone, t: "Sales Teams", d: "Reach verified, active businesses with confirmed GST registrations — real, operating companies with buying power. Target by state, city, industry or turnover." },
  { icon: TrendingUp, t: "Marketing Agencies", d: "Build hyper-targeted B2B ad campaigns on Google, Facebook and LinkedIn using GST data. Segment audiences by business category, location and size." },
  { icon: Landmark, t: "Banks & NBFCs", d: "Offer business loans, working capital, current accounts and credit lines to GST-registered businesses. GSTIN confirms the business is legitimate and tax-compliant." },
  { icon: ShieldPlus, t: "Insurance Companies", d: "Pitch commercial insurance — fire, liability, group health, marine — to verified businesses. GST data confirms they're active and operational." },
  { icon: Cpu, t: "Software & ERP", d: "Sell GST billing software, accounting tools, ERP systems and inventory management to businesses that need GST compliance tools." },
  { icon: PenTool, t: "Trademark & IP", d: "Target GST-registered businesses that likely need trademark registration, brand protection and intellectual property services." },
  { icon: Scale, t: "CA & Tax Firms", d: "Reach businesses for GST return filing, tax planning, audit and compliance services. Every GST-registered business needs regular filing assistance." },
  { icon: Briefcase, t: "Business Consultants", d: "Offer advisory on GST compliance, input tax credit optimization, e-invoicing setup and regulatory compliance." },
];

const PRICING = [
  { t: "Trial Pack", price: "₹3,000", period: "one-time", d: "1-day GST data sample, up to 500 records. Excel/CSV — verify quality before purchasing.", cta: "Order Trial", href: WA_URL, external: true, featured: false },
  { t: "Monthly Plan", price: "₹30,000", period: "per month", d: "Daily GST data delivery, all states, all business types, unlimited records. Dedicated WhatsApp support.", cta: "Order Now", href: WA_URL, external: true, featured: true, badge: "Most Popular" },
  { t: "Annual Plan", price: "Contact for Best Price", period: "12 months", d: "12 months of daily GST data, priority delivery, custom state/industry filters. API access available.", cta: "Get Quote", href: "/contact", external: false, featured: false },
];

const SAMPLE_ROWS = [
  { c: "Sharma Traders", g: "09AABCS1234A1ZF", o: "Vikram Sharma", m: "98765XXXXX", e: "vikram@sharmatraders.in", s: "Uttar Pradesh", cat: "Trading", d: "12-Jan-2026" },
  { c: "TechNova Solutions", g: "29AABCT5678B1ZM", o: "Deepika Nair", m: "87654XXXXX", e: "deepika@technova.co.in", s: "Karnataka", cat: "Services", d: "15-Jan-2026" },
  { c: "GreenLeaf Organics", g: "27AABCG9012C1ZK", o: "Anand Patil", m: "90123XXXXX", e: "anand@greenleaf.in", s: "Maharashtra", cat: "Manufacturing", d: "18-Jan-2026" },
  { c: "Royal Textiles", g: "24AABCR3456D1ZJ", o: "Fatima Khan", m: "81234XXXXX", e: "fatima@royaltextiles.in", s: "Gujarat", cat: "Manufacturing", d: "20-Jan-2026" },
  { c: "Digital Bazaar", g: "07AABCD7890E1ZH", o: "Rohit Mehra", m: "93456XXXXX", e: "rohit@digitalbazaar.in", s: "Delhi", cat: "Trading", d: "22-Jan-2026" },
  { c: "Nilgiri Spice Co.", g: "33AABCN2345F1ZP", o: "Meera Krishnan", m: "94567XXXXX", e: "meera@nilgirispice.in", s: "Tamil Nadu", cat: "Manufacturing", d: "25-Jan-2026", blur: true },
  { c: "Coastal Marine Exports", g: "32AABCC6789G1ZL", o: "Joseph Xavier", m: "89012XXXXX", e: "joseph@coastalmarine.in", s: "Kerala", cat: "Trading", d: "27-Jan-2026", blur: true },
  { c: "Northlight Interiors LLP", g: "08AABCN3456H1ZQ", o: "Aarti Malhotra", m: "82345XXXXX", e: "aarti@northlight.in", s: "Rajasthan", cat: "Services", d: "28-Jan-2026", blur: true },
];

const COMPARISON = [
  { f: "Data Freshness", d: "Daily Updated", a: "Weekly", b: "Monthly", cc: "Quarterly" },
  { f: "Owner Mobile", d: "Verified", a: "—", b: "Partial", cc: "—" },
  { f: "Owner Email", d: "Yes", a: "—", b: "Yes", cc: "—" },
  { f: "GSTIN Verified", d: "Yes", a: "Yes", b: "Yes", cc: "—" },
  { f: "District / City Level", d: "Yes", a: "—", b: "—", cc: "State only" },
  { f: "Business Category Filter", d: "Yes", a: "—", b: "Yes", cc: "—" },
  { f: "Turnover Slab Data", d: "Yes", a: "—", b: "—", cc: "—" },
  { f: "Filing Status", d: "Yes", a: "—", b: "—", cc: "—" },
  { f: "Excel / CSV", d: "Yes", a: "Yes", b: "PDF only", cc: "Yes" },
  { f: "WhatsApp Support", d: "Yes", a: "—", b: "—", cc: "Email only" },
  { f: "Trial Available", d: "Yes — ₹3,000", a: "—", b: "—", cc: "—" },
];

const STATE_COUNTS = [
  ["Maharashtra","18,00,000+"],["Uttar Pradesh","14,50,000+"],["Tamil Nadu","12,00,000+"],
  ["Gujarat","11,50,000+"],["Karnataka","10,80,000+"],["Rajasthan","9,20,000+"],
  ["West Bengal","8,50,000+"],["Delhi","8,00,000+"],["Madhya Pradesh","7,20,000+"],
  ["Haryana","6,80,000+"],["Telangana","6,50,000+"],["Andhra Pradesh","6,00,000+"],
  ["Punjab","5,50,000+"],["Kerala","5,00,000+"],["Bihar","4,80,000+"],
  ["Odisha","3,50,000+"],["Jharkhand","2,80,000+"],["Chhattisgarh","2,50,000+"],
  ["Assam","2,20,000+"],["Uttarakhand","2,00,000+"],["Himachal Pradesh","1,50,000+"],
  ["Goa","80,000+"],["Tripura","50,000+"],["Meghalaya","40,000+"],
  ["Manipur","35,000+"],["Nagaland","25,000+"],["Arunachal Pradesh","20,000+"],
  ["Mizoram","18,000+"],["Sikkim","15,000+"],
];
const UT_COUNTS = [
  ["Chandigarh","1,20,000+"],["Jammu & Kashmir","2,50,000+"],["Puducherry","60,000+"],
  ["Ladakh","5,000+"],["Andaman & Nicobar","8,000+"],["Lakshadweep","2,000+"],
  ["Dadra & NH and DD","40,000+"],
];

const FAQS = [
  { q: "What is GST registered company database?", a: "A GST registered company database is a structured collection of business details of all entities registered under the Goods and Services Tax system in India. It includes business name, GSTIN, owner name, mobile, email, state, business category, and registration date. This data is invaluable for B2B sales, marketing, and business development." },
  { q: "How to download GST registered companies list in India?", a: "You can purchase and download GST registered company data from Datorra India. We deliver the data in Excel (.xlsx) and CSV formats. Contact us on WhatsApp at +91 85379 42196 to place your order and receive the data within hours." },
  { q: "What is the difference between GST database and MCA database?", a: "GST database covers all tax-registered businesses including proprietorships, partnerships, HUFs, and trusts — approximately 1.5 crore+ entities. MCA database only covers incorporated companies (Pvt Ltd, LLP, OPC, Public Ltd) — about 30 lakh+ entities. GST data gives broader coverage; MCA data gives deeper company information." },
  { q: "Is GSTIN database legal to purchase?", a: "Yes, purchasing GST registration data is legal. GSTIN information is based on publicly available registration records. Using this data for legitimate B2B marketing, sales outreach, and business development is completely legal in India." },
  { q: "How many businesses register for GST every month?", a: "India sees 50,000 to 80,000 new GST registrations every month. The number has been growing year-over-year — from 62,500/month in 2022 to over 1 lakh/month projected in 2026. This creates a massive, continuously growing pool of business leads." },
  { q: "What is the price of GST company database?", a: "Our GST database pricing starts at ₹3,000 for a trial pack (1-day data, up to 500 records). The monthly plan is ₹30,000/month for daily data delivery with pan-India coverage. Annual plans are available at discounted rates — contact us for custom pricing." },
  { q: "Can I filter GST data by state, city, or industry?", a: "Yes, Datorra offers filtering by state, district/city, business category (manufacturing/trading/services), business type (proprietorship/partnership/Pvt Ltd etc.), and turnover slab. You can request precisely the data segment you need." },
  { q: "What format is GST data delivered in?", a: "GST data is delivered in Excel (.xlsx) and CSV formats. These are universally compatible with spreadsheet software (Excel, Google Sheets), CRM tools (Salesforce, HubSpot, Zoho), and calling/dialer software." },
  { q: "How accurate is Datorra's GST database?", a: "Our GST data is sourced from verified registration records and updated daily. Mobile numbers and emails are as filed in the GST registration. We maintain over 90% accuracy on contact details. We offer a trial pack so you can verify quality before committing." },
  { q: "Can I get GST data for newly registered businesses only?", a: "Yes, that is our core offering. Datorra delivers data of newly registered GST businesses on a daily basis. You receive yesterday's and today's new registrations, giving you the freshest leads in the market." },
];

const EXPLORE = [
  { l: "MCA Company Database India", h: "/mca-company-database" },
  { l: "GST Sales & Purchase Transaction Data", h: "/gst-sales-purchase-data" },
  { l: "GST Data by Month", h: "/gst-data" },
  { l: "Contact Our Sales Team", h: "/contact" },
];

export default function GstDatabase() {
  const [openFaq, setOpenFaq] = useState(0);

  useSeo({
    title: "GST Registered Company Database India | B2B Leads | Download Excel | Datorra",
    description:
      "Buy verified GST registered company database in India. Fresh GST registrations with business name, GSTIN, owner mobile, email. Pan-India. Download Excel/CSV. Call +91 85379 42196",
    path: "/gst-database",
    jsonLd: [
      {
        id: "gstdb-breadcrumb",
        data: {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://datorra.in/" },
            { "@type": "ListItem", position: 2, name: "GST Database", item: "https://datorra.in/gst-database" },
          ],
        },
      },
      {
        id: "gstdb-product",
        data: {
          "@context": "https://schema.org",
          "@type": "Product",
          name: "GST Registered Company Database India",
          description:
            "Verified GST registered company database — 1.5 crore+ Indian businesses with GSTIN, owner mobile, email, state, category, filing status. Daily updated, delivered in Excel/CSV.",
          brand: { "@type": "Brand", name: "Datorra India" },
          category: "Business Data / GST Database",
          areaServed: "IN",
          offers: [
            { "@type": "Offer", name: "Trial Pack", price: "3000", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://datorra.in/gst-database" },
            { "@type": "Offer", name: "Monthly Plan", price: "30000", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://datorra.in/gst-database" },
            { "@type": "Offer", name: "Annual Plan", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://datorra.in/gst-database" },
          ],
        },
      },
      {
        id: "gstdb-faq",
        data: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      },
    ],
  });

  const scrollToPricing = () => {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="gst-database-page" className="bg-white">
      {/* HERO */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-20 border-b border-slate-100 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-blue-50/60 blur-3xl rounded-full pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <nav aria-label="Breadcrumb" className="text-xs md:text-sm text-slate-500 flex items-center gap-1.5">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" strokeWidth={1.75} />
            <span className="text-slate-900 font-medium">GST Database</span>
          </nav>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-slate-900 leading-[1.03] max-w-5xl"
          >
            GST Registered Company Database India — <span className="font-serif-editorial italic">50,000+</span> new registrations every month
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="mt-6 max-w-3xl text-lg md:text-xl text-slate-600 leading-relaxed"
          >
            Access verified GST registration data of businesses across India. Get business name, <strong className="text-slate-900">GSTIN</strong>, owner name, mobile,
            email and complete registration details. Fresh data updated daily. Delivered in Excel/CSV. Trusted by sales teams,
            marketing agencies and financial institutions.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" data-testid="gstdb-hero-sample" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-500/20 active:scale-95 transition-all">
              Get Sample Data
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
            </a>
            <button onClick={scrollToPricing} data-testid="gstdb-hero-pricing" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 border border-slate-200 pl-6 pr-5 py-4 text-sm font-medium hover:border-slate-900 hover:bg-slate-50 active:scale-95 transition-all">
              View Pricing
              <ChevronRight className="w-4 h-4 rotate-90" strokeWidth={1.75} />
            </button>
          </motion.div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-600">
            {["Daily Updated", "13 Data Fields", "1.5 Cr+ Businesses", "Excel / CSV"].map((b) => (
              <span key={b} className="inline-flex items-center gap-2">
                <Check className="w-4 h-4 text-blue-600" strokeWidth={2.25} />
                <span className="font-medium text-slate-800">{b}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — What is */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Overview</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            What is <span className="font-serif-editorial italic">GST Registered Company Database</span>?
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-8">
            {[
              { icon: Percent, t: "Mandatory Above ₹40 Lakh", d: "GST (Goods and Services Tax) registration is mandatory for businesses with turnover above ₹40 lakh — ₹20 lakh for services." },
              { icon: Fingerprint, t: "Unique 15-digit GSTIN", d: "Every GST-registered business gets a unique 15-digit GSTIN — a permanent, verifiable identifier." },
              { icon: Building2, t: "Wider Than MCA Data", d: "GST covers all tax-registered entities — proprietorships to large corporations — far broader than MCA (which is only incorporated companies)." },
              { icon: TrendingUp, t: "Tax-Paying, Active Businesses", d: "GST data represents active, tax-paying businesses — ideal for B2B marketing because these are real, operating companies with buying power." },
              { icon: Cpu, t: "Verified Contact Details", d: "Datorra ships this data in clean, structured format with verified mobile numbers, emails and complete GSTIN records." },
              { icon: Activity, t: "50,000+ New Every Month", d: "50,000 to 80,000 new businesses register for GST every single month — a continuously renewing lead pool." },
            ].map((r) => (
              <div key={r.t} className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                  <r.icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-display text-lg font-medium text-slate-900">{r.t}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{r.d}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-500/20 active:scale-95 transition-all">
              Start Reaching GST Registered Businesses
              <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 3 — GST vs MCA */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Compare</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            GST Database vs MCA Database — which do you need?
          </h2>

          <div className="mt-12 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-[0.14em]">
                  <th className="p-5 font-medium text-slate-500">Aspect</th>
                  <th className="p-5 font-display font-medium text-slate-900 bg-blue-50 border-l border-blue-100">GST Database</th>
                  <th className="p-5 font-medium text-slate-700">MCA Database</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {[
                  ["Source","GST Portal (GSTN)","Ministry of Corporate Affairs"],
                  ["Covers","All tax-registered businesses","Only incorporated companies"],
                  ["Business Types","Proprietorship, Partnership, Pvt Ltd, LLP, Trust, HUF","Pvt Ltd, LLP, OPC, Public Ltd only"],
                  ["Volume","1.5 crore+ total registrations","30 lakh+ companies"],
                  ["New Monthly","50,000-80,000","50,000+"],
                  ["Best For","Broad B2B outreach, SME targeting","Formal companies, director outreach"],
                  ["Key Field","GSTIN","CIN"],
                  ["Contact","Owner / Partner mobile","Director mobile"],
                ].map((r, i) => (
                  <tr key={r[0]} className={`border-t border-slate-100 ${i % 2 === 1 ? "bg-slate-50/40" : "bg-white"}`}>
                    <td className="p-5 font-medium text-slate-900">{r[0]}</td>
                    <td className="p-5 bg-blue-50/60 border-l border-blue-100 text-slate-900">{r[1]}</td>
                    <td className="p-5">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-8 max-w-3xl text-slate-600 text-base md:text-lg leading-relaxed">
            Use GST data when you want broader coverage including small businesses and proprietorships. Use MCA data when
            you specifically target incorporated companies and their directors. For maximum coverage, use both together.
          </p>

          <div className="mt-8">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-500/20 transition-all">
              Get Both GST &amp; MCA Data
              <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 4 — Data Fields */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Data Fields</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Complete data fields in our GST database
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FIELDS.map((f, i) => (
              <div key={f.name} className="bg-[#fafafa] border border-slate-100 rounded-2xl p-5 flex items-start gap-4 hover:bg-white hover:border-blue-100 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                  <f.icon className="w-4 h-4 text-blue-600" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-medium">/{String(i + 1).padStart(2, "0")}</span>
                    <div className="font-display text-base font-medium text-slate-900">{f.name}</div>
                  </div>
                  <div className="mt-1.5 text-sm text-slate-500 leading-relaxed">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — Stats & Yearly Table */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Growth</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            GST registration growth in India — year-wise
          </h2>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {HERO_STATS.map((s) => (
              <div key={s.n} className="bg-white border border-slate-100 rounded-2xl p-6">
                <div className="font-display text-2xl md:text-3xl font-light text-slate-900">{s.n}</div>
                <div className="mt-2 text-xs text-slate-500 leading-relaxed">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="p-4 font-medium">Year</th>
                  <th className="p-4 font-medium">Total New GST Registrations</th>
                  <th className="p-4 font-medium">Monthly Avg</th>
                  <th className="p-4 font-medium">Growth</th>
                </tr>
              </thead>
              <tbody>
                {STATS_YEAR.map((r, i) => (
                  <tr key={r.y} className={`border-t border-slate-100 ${i % 2 === 1 ? "bg-white" : "bg-slate-50/40"}`}>
                    <td className="p-4 font-medium text-slate-900">{r.y}</td>
                    <td className="p-4 text-slate-700">{r.r}</td>
                    <td className="p-4 text-slate-700">{r.m}</td>
                    <td className="p-4 text-blue-600 font-medium">{r.g}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-slate-400">Source: GSTN public reports and industry estimates.</p>
        </div>
      </section>

      {/* SECTION 6 — Use Cases */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Use Cases</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Who uses GST registered company database?
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {USE_CASES.map((u) => (
              <div key={u.t} className="bg-[#fafafa] border border-slate-100 rounded-3xl p-7 hover:bg-white hover:shadow-md hover:border-blue-100 transition-all flex flex-col">
                <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <u.icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 font-display text-lg font-medium text-slate-900">{u.t}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — Pricing */}
      <section id="pricing" className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Pricing</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            GST database pricing — <span className="font-serif-editorial italic">transparent</span> &amp; competitive
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {PRICING.map((p) => (
              <div key={p.t} className={`relative rounded-3xl p-8 flex flex-col ${p.featured ? "bg-slate-900 text-white border border-slate-900 shadow-2xl shadow-slate-900/20" : "bg-white border border-slate-100"}`}>
                {p.badge && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 rounded-full bg-blue-600 text-white px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold">
                    {p.badge}
                  </span>
                )}
                <h3 className={`font-display text-2xl font-medium ${p.featured ? "text-white" : "text-slate-900"}`}>{p.t}</h3>
                <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                  <span className={`font-display text-3xl md:text-4xl font-light ${p.featured ? "text-white" : "text-slate-900"}`}>{p.price}</span>
                  <span className={`text-sm ${p.featured ? "text-slate-300" : "text-slate-500"}`}>{p.period}</span>
                </div>
                <p className={`mt-4 text-sm leading-relaxed ${p.featured ? "text-slate-300" : "text-slate-600"}`}>{p.d}</p>
                {p.external ? (
                  <a href={p.href} target="_blank" rel="noopener noreferrer" className={`mt-8 group inline-flex items-center gap-2 rounded-full pl-5 pr-4 py-3 text-sm font-medium self-start transition-all ${p.featured ? "bg-white text-slate-900 hover:bg-blue-600 hover:text-white" : "bg-slate-900 text-white hover:bg-blue-600"}`}>
                    {p.cta}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                  </a>
                ) : (
                  <Link to={p.href} className={`mt-8 group inline-flex items-center gap-2 rounded-full pl-5 pr-4 py-3 text-sm font-medium self-start transition-all ${p.featured ? "bg-white text-slate-900 hover:bg-blue-600 hover:text-white" : "bg-slate-900 text-white hover:bg-blue-600"}`}>
                    {p.cta}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                  </Link>
                )}
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-slate-500 text-sm">
            All prices exclusive of GST. Custom packages available for state-specific, city-specific or industry-specific data.
          </p>
        </div>
      </section>

      {/* SECTION 8 — Sample */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Sample</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Preview our GST database quality
          </h2>

          <div className="mt-12 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[1000px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                  {["Business Name","GSTIN","Owner","Mobile","Email","State","Category","Reg Date"].map((h) => (
                    <th key={h} className="p-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SAMPLE_ROWS.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className={`p-4 text-slate-900 font-medium ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.c}</td>
                    <td className={`p-4 text-slate-700 font-mono text-xs ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.g}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.o}</td>
                    <td className={`p-4 text-slate-700 font-mono text-xs ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.m}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.e}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.s}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.cat}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 flex flex-col items-center gap-3 text-center">
            <p className="text-slate-600">Like what you see? Request a complete, unblurred sample.</p>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-500/20 transition-all">
              Request Sample on WhatsApp
              <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 9 — Competitor comparison */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Why Datorra</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Why Datorra is India's preferred GST data provider
          </h2>

          <div className="mt-12 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-[0.14em]">
                  <th className="p-5 font-medium text-slate-500">Feature</th>
                  <th className="p-5 font-display font-medium text-slate-900 bg-blue-50 border-l border-blue-100">
                    <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-600" />Datorra India</span>
                  </th>
                  <th className="p-5 font-medium text-slate-500">Provider A</th>
                  <th className="p-5 font-medium text-slate-500">Provider B</th>
                  <th className="p-5 font-medium text-slate-500">Provider C</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.f} className={`border-t border-slate-100 ${i % 2 === 1 ? "bg-slate-50/40" : "bg-white"}`}>
                    <td className="p-5 font-medium text-slate-700">{row.f}</td>
                    <td className="p-5 bg-blue-50/60 border-l border-blue-100">
                      <span className="inline-flex items-center gap-2 text-slate-900 font-medium">
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" strokeWidth={2.25} />
                        {row.d}
                      </span>
                    </td>
                    {[row.a, row.b, row.cc].map((v, j) => (
                      <td key={j} className="p-5 text-slate-500">
                        {v === "—" ? <span className="text-slate-300">—</span> : <span>{v}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SECTION 10 — Coverage */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Coverage</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            State-wise GST registration data — Pan India coverage
          </h2>

          <h3 className="mt-12 text-sm uppercase tracking-[0.16em] font-semibold text-slate-500">States (29)</h3>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {STATE_COUNTS.map(([s, n]) => (
              <div key={s} className="flex items-center justify-between rounded-xl bg-[#fafafa] border border-slate-100 px-4 py-3 hover:border-blue-100 hover:bg-white transition-colors">
                <span className="inline-flex items-center gap-2 text-sm text-slate-800">
                  <MapPin className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" strokeWidth={1.75} />
                  <span className="truncate">{s}</span>
                </span>
                <span className="text-xs font-medium text-blue-700 whitespace-nowrap">{n}</span>
              </div>
            ))}
          </div>

          <h3 className="mt-10 text-sm uppercase tracking-[0.16em] font-semibold text-slate-500">Union Territories (7)</h3>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {UT_COUNTS.map(([s, n]) => (
              <div key={s} className="flex items-center justify-between rounded-xl bg-[#fafafa] border border-slate-100 px-4 py-3">
                <span className="inline-flex items-center gap-2 text-sm text-slate-800">
                  <MapPin className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" strokeWidth={1.75} />
                  <span className="truncate">{s}</span>
                </span>
                <span className="text-xs font-medium text-blue-700 whitespace-nowrap">{n}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-400">
            Numbers are approximate based on public GSTN data. Actual records in our database are updated daily.
          </p>
        </div>
      </section>

      {/* SECTION 11 — FAQ */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ FAQ</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Frequently asked questions about GST company database
          </h2>

          <div className="mt-12 divide-y divide-slate-200">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="py-1">
                  <button
                    data-testid={`gstdb-faq-toggle-${i}`}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    aria-expanded={open}
                    className="w-full py-6 flex items-start justify-between gap-6 text-left group"
                  >
                    <span className="font-display text-lg md:text-xl font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                      {f.q}
                    </span>
                    <span className="mt-1 flex-shrink-0 w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center transition-colors group-hover:border-slate-900">
                      {open ? <Minus className="w-4 h-4 text-slate-900" strokeWidth={1.75} /> : <Plus className="w-4 h-4 text-slate-900" strokeWidth={1.75} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.2, 0.7, 0.15, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 pr-16 text-slate-600 leading-relaxed text-base md:text-lg">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 12 — Long-form SEO Content */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Playbook</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            How to use GST registered company database for B2B marketing in India
          </h2>

          <div className="mt-12 space-y-10 text-base md:text-lg text-slate-700 leading-relaxed">
            <p>
              India's GST ecosystem is the largest, cleanest B2B contact universe in the country. Over 1.5 crore businesses
              are actively registered under GST, and 50,000-80,000 new registrations happen every single month. For any B2B
              team — from SaaS to financial services — a well-scoped <strong className="text-slate-900">gst registered company database india</strong>{" "}
              is not a nice-to-have; it's the fastest path from cold market to warm pipeline.
            </p>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Understanding GST registration data — a goldmine for marketers</h3>
              <p className="mt-4">
                A <strong className="text-slate-900">gst company list india</strong> isn't just a list — it's a proof-of-life signal.
                Every GSTIN is a business that has registered with the government, is paying taxes and is actively transacting.
                Compared to scraped web lists or purchased email dumps, a <strong className="text-slate-900">verified gst company data</strong>{" "}
                set gives you three things: a legally clean identifier (GSTIN), a filed contact (owner or authorised signatory)
                and a clear category (manufacturing, trading, services). That combination is why growth teams increasingly
                <strong className="text-slate-900"> buy gst database india</strong> access rather than piecing lists together manually.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Scale — why 1.5 crore matters</h3>
              <p className="mt-4">
                A pan india gst database of this size means you can build almost any ICP intersection you want — Pvt Ltd
                manufacturers in Gujarat above ₹5 crore turnover, service proprietorships in Delhi below ₹1 crore, LLPs in
                Bangalore in the IT category. As a <strong className="text-slate-900">gst data provider india</strong> teams rely on,
                Datorra makes those cuts trivial: state, city, category, business type, turnover slab and filing status —
                all filterable from the same base table.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">GST data vs MCA data — when to use which</h3>
              <p className="mt-4">
                A GSTIN database gives width; an MCA database gives depth. Use{" "}
                <strong className="text-slate-900">gst number database india</strong> when you want to reach every kind of business
                — from a proprietorship in a Tier-3 town to a large public company. Use MCA when you specifically want to
                target directors of incorporated entities. Most enterprise outbound teams run both in parallel:
                the <strong className="text-slate-900">gst holder database india</strong> for volume plays, MCA for
                account-based motions with named-account targeting.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Step-by-step guide — building a B2B campaign on GST data</h3>
              <h4 className="mt-6 font-display text-lg font-medium text-slate-900">Step 1 — Define your ICP</h4>
              <p className="mt-3">
                State, city, business category (manufacturing / trading / services), business type, turnover slab, filing
                cadence. Write it down as a one-sentence definition and validate with 25-50 sample records before you
                <strong className="text-slate-900"> download gst registered company list excel</strong> in bulk.
              </p>
              <h4 className="mt-6 font-display text-lg font-medium text-slate-900">Step 2 — Filter tightly</h4>
              <p className="mt-3">
                Apply four filters at most. Over-filtering shrinks the list; under-filtering wastes calls. A good starting
                cut for a ₹30 lakh-₹5 crore SaaS product is: any Pvt Ltd or LLP, in metro states, in Services, with active
                filing status.
              </p>
              <h4 className="mt-6 font-display text-lg font-medium text-slate-900">Step 3 — Build segmented contact lists</h4>
              <p className="mt-3">
                Split the file into three tiers by recency — hot (registered in last 7 days), warm (last 8-30 days), cold
                (30+ days). Recent registrations are hungriest — they're actively evaluating vendors. Route them to your
                senior reps.
              </p>
              <h4 className="mt-6 font-display text-lg font-medium text-slate-900">Step 4 — Multi-channel outreach</h4>
              <p className="mt-3">
                Cold call, email, WhatsApp and LinkedIn together — not one channel at a time. A four-day sequence typically
                converts 3-4x better than any single-channel play. Because you have owner mobile from the{" "}
                <strong className="text-slate-900">gst company contact list</strong>, skip the gatekeeper on day one.
              </p>
              <h4 className="mt-6 font-display text-lg font-medium text-slate-900">Step 5 — Track and optimise</h4>
              <p className="mt-3">
                Tag every lead by state, category and turnover slab. After 60 days, look at which segments convert and
                double down. Move low-converting cohorts to a slower nurture cadence.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Industry-specific strategies</h3>
              <p className="mt-4">
                <strong className="text-slate-900">SaaS and software</strong> — target service GSTINs in Tier-1 cities, focus on
                new registrations for founder-led evaluations.{" "}
                <strong className="text-slate-900">Banks and NBFCs</strong> — filter turnover slabs above ₹1 crore for current
                account and working capital plays; below ₹1 crore for MSME loans.{" "}
                <strong className="text-slate-900">Insurance</strong> — manufacturing GSTINs for fire and marine; services for
                group health.<strong className="text-slate-900"> CA firms</strong> — the freshest cohort of{" "}
                <strong className="text-slate-900">new gst registration data india</strong> for GST filing packages.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Why fresh data beats old databases</h3>
              <p className="mt-4">
                A six-month-old GSTIN list is a graveyard — the businesses have picked their CA, opened their bank account
                and finalised their software stack. That's why the <strong className="text-slate-900">gst registered companies list download</strong>{" "}
                you buy needs to be updated daily. Datorra refreshes the base every 24 hours so your team is always
                working the freshest cohort.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Common mistakes B2B marketers make</h3>
              <p className="mt-4">
                Buying stale lists. Skipping the sample. Filtering too aggressively. Running a single-channel play. Not
                honouring DND registrations. Not tagging results in the CRM. Each of these can quietly kill an otherwise
                great <strong className="text-slate-900">gst registered business list</strong>.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Compliance and best practices</h3>
              <p className="mt-4">
                GSTIN registration data is public information. Using it for legitimate B2B outreach is legal. But respect
                the recipient — honour DND, keep opt-outs current, keep messages relevant and sign a data-use agreement
                with your provider. That's how a professional B2B team turns a GST data purchase into an outbound engine
                that lasts years.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">The future of B2B data in India</h3>
              <p className="mt-4">
                E-invoicing thresholds keep dropping — soon every business above ₹5 crore turnover will be issuing e-invoices.
                That means <strong className="text-slate-900">gst b2b leads india</strong> will become even richer, layered
                with real transaction data. Teams that build their outbound motion on daily-updated GSTIN data today are
                setting themselves up for that future.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Conclusion</h3>
              <p className="mt-4">
                A well-scoped GST database is one of the highest-leverage assets a B2B team can own in India. If you're
                ready to start, request a sample from Datorra on WhatsApp — you'll have your first cohort in Excel within hours.
              </p>
              <div className="mt-6">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-500/20 transition-all">
                  Get Started Today
                  <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 13 — Explore More */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ More</div>
          <h2 className="mt-4 font-display text-2xl md:text-4xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Explore more data products
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {EXPLORE.map((l) => (
              <Link key={l.l} to={l.h} className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-5 hover:border-slate-900 hover:shadow-md transition-all">
                <span className="font-medium text-slate-900 text-sm">{l.l}</span>
                <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" strokeWidth={1.75} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA BANNER */}
      <section className="relative py-20 md:py-24 bg-slate-900 text-white overflow-hidden">
        <div className="absolute -top-16 -left-16 w-96 h-96 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
            Ready to access India's <span className="font-serif-editorial italic">most verified</span> GST company database?
          </h2>
          <p className="mt-6 text-blue-200">Daily GST data delivery — trusted by sales teams, marketing agencies and financial institutions.</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-7 pr-6 py-5 text-base font-medium hover:brightness-95 hover:shadow-xl hover:shadow-green-500/25 active:scale-95 transition-all">
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} />
              Talk to Sales on WhatsApp
            </a>
            <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 pl-7 pr-6 py-5 text-base font-medium hover:bg-blue-50 hover:text-blue-700 active:scale-95 transition-all">
              Request Access
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
